import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import DateSelect from '../components/ui/DateSelect';
import Icons from '../components/Icons';
import BulkActionBar from '../components/inventory/BulkActionBar';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import SearchableSelect from '../components/ui/SearchableSelect';
import PackDisabledPanel from '../components/PackDisabledPanel';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import { useUser } from '../store/UserContext';
import { useFeaturePacks } from '../store/FeaturePackContext';
import AttendanceService from '../services/AttendanceService';
import StaffService from '../services/StaffService';
import MessageService from '../services/MessageService';
import { formatDate } from '../utils/dateFormat';
import { readFileAsStoredAttachment } from '../utils/fileAttachments';

const riskOrder = { critical: 4, high: 3, medium: 2, low: 1 };

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const buildSafeguardingEvents = (student) => ([
  ...(student.safeguardingEvents || []).map((event) => ({ ...event, categoryLabel: 'Safeguarding' })),
  ...(student.medicalEvents || []).map((event) => ({ ...event, categoryLabel: 'Medical' })),
  ...(student.policeEvents || []).map((event) => ({ ...event, categoryLabel: 'Police' }))
]);

const isActiveSafeguarding = (event) => event.status === 'open' || event.status === 'monitoring';

const getDocumentAlerts = (documents = []) => {
  const now = new Date();
  const dueSoonThreshold = new Date();
  dueSoonThreshold.setDate(now.getDate() + 7);
  const dueLaterThreshold = new Date();
  dueLaterThreshold.setDate(now.getDate() + 30);

  return documents.map((doc) => {
    const dueValue = doc.dueDate || doc.metadata?.dueDate;
    const expiryValue = doc.expiryDate || doc.metadata?.expiryDate;
    const dueDate = parseDateValue(dueValue);
    const expiryDate = parseDateValue(expiryValue);
    const date = dueDate || expiryDate;
    let status = 'compliant';
    if (!date) status = 'missing';
    if (date && date < now) status = 'overdue';
    if (date && date >= now && date <= dueSoonThreshold) status = 'due-soon';
    if (date && date > dueSoonThreshold && date <= dueLaterThreshold) status = 'due-30';
    return { ...doc, dueDate: dueValue, expiryDate: expiryValue, status };
  });
};

const statusMeta = {
  overdue: { label: 'Overdue', variant: 'danger' },
  'due-soon': { label: 'Due <7 days', variant: 'warning' },
  'due-30': { label: 'Due <30 days', variant: 'warning' },
  missing: { label: 'Missing', variant: 'danger' },
  compliant: { label: 'Compliant', variant: 'active' }
};
const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function StudentsPage() {
  const { students, controller } = useStudentStore();
  const { user } = useUser();
  const { packs } = useFeaturePacks();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const attendanceService = useMemo(() => new AttendanceService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const messageService = useMemo(() => new MessageService(), []);
  const staffList = useMemo(() => safeList(staffService), [staffService]);
  const staffOptions = useMemo(
    () => staffList.map((staff) => ({ value: staff.id, label: staff.name, meta: staff.role || '' })),
    [staffList]
  );

  const viewParam = searchParams.get('view') || 'all';
  const mode = viewParam === 'compliance' ? 'compliance' : viewParam === 'safeguarding' ? 'safeguarding' : 'students';
  const blockedPackName =
    mode === 'compliance' && packs?.compliance === false
      ? 'Compliance'
      : mode === 'safeguarding' && packs?.safeguarding_pro === false
        ? 'Safeguarding Pro'
        : '';

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    risk: 'all',
    attendance: 'all',
    status: 'all',
    safeguarding: 'all'
  });
  const [activeKpi, setActiveKpi] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeItemId, setActiveItemId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTargetId, setAssignTargetId] = useState('');
  const [reviewDateModal, setReviewDateModal] = useState(false);
  const [reviewDate, setReviewDate] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [complianceNoteDraft, setComplianceNoteDraft] = useState('');
  const [complianceNoteStatus, setComplianceNoteStatus] = useState('');
  const complianceFileRef = useRef(null);

  const { settings } = useSettings();
  const attendanceThreshold = Number(settings.attendanceThreshold || 70);

  useEffect(() => {
    if (mode !== 'students') return;
    if (viewParam === 'high-risk') {
      setFilters((prev) => ({ ...prev, risk: 'high' }));
    } else if (viewParam === 'attendance') {
      setFilters((prev) => ({ ...prev, attendance: 'below' }));
    } else if (viewParam === 'in-review') {
      setFilters((prev) => ({ ...prev, status: 'Under Review' }));
    }
  }, [viewParam, mode]);

  const studentRows = useMemo(() => {
    const filtered = students.filter((student) => {
      const matchesSearch =
        !searchQuery ||
        [student.name, student.reference, student.email]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(searchQuery.toLowerCase()));

      if (!matchesSearch) return false;

      if (filters.risk !== 'all') {
        if (filters.risk === 'high') {
          if (!['high', 'critical'].includes(student.riskLevel)) return false;
        } else if (student.riskLevel !== filters.risk) return false;
      }

      if (filters.attendance === 'below' && !attendanceService.isBelowThreshold(student, attendanceThreshold)) {
        return false;
      }

      if (filters.status !== 'all' && student.status !== filters.status) return false;

      if (filters.safeguarding === 'active') {
        const hasOpen = buildSafeguardingEvents(student).some((event) => isActiveSafeguarding(event));
        if (!hasOpen) return false;
      }

      if (activeKpi === 'critical' && student.riskLevel !== 'critical') return false;
      if (activeKpi === 'attendance' && !attendanceService.isBelowThreshold(student, attendanceThreshold)) return false;
      if (activeKpi === 'safeguarding') {
        const hasOpen = buildSafeguardingEvents(student).some((event) => isActiveSafeguarding(event));
        if (!hasOpen) return false;
      }

      return true;
    });

    return filtered.sort((a, b) => {
      const riskDelta = riskOrder[b.riskLevel] - riskOrder[a.riskLevel];
      if (riskDelta !== 0) return riskDelta;
      const attendanceDelta = (a.attendance?.rate || 0) - (b.attendance?.rate || 0);
      if (attendanceDelta !== 0) return attendanceDelta;
      return a.name.localeCompare(b.name);
    });
  }, [students, searchQuery, filters, activeKpi, attendanceService, attendanceThreshold]);

  const studentKpis = useMemo(() => {
    const total = students.length;
    const critical = students.filter((s) => s.riskLevel === 'critical').length;
    const attendanceBelow = students.filter((s) => attendanceService.isBelowThreshold(s, attendanceThreshold)).length;
    const safeguardingActive = students.filter((s) => buildSafeguardingEvents(s).some((e) => isActiveSafeguarding(e))).length;
    return [
      { id: 'total', label: 'Total students', value: total },
      { id: 'critical', label: 'Critical risk', value: critical, badge: { label: 'Immediate', variant: 'danger' } },
      { id: 'attendance', label: `Attendance < ${attendanceThreshold}%`, value: attendanceBelow },
      { id: 'safeguarding', label: 'Safeguarding active', value: safeguardingActive, badge: { label: 'Active', variant: 'warning' } }
    ];
  }, [students, attendanceService, attendanceThreshold]);

  const complianceItems = useMemo(() => {
    if (mode !== 'compliance') return [];
    return students.flatMap((student) => {
    const docs = [...(student.documents || []), ...(student.certificates || [])]
      .filter((doc) => !doc?.metadata?.complianceResolved);
    if (!docs.length) {
      return [{
        id: `comp-${student.id}-missing`,
        studentId: student.id,
        studentName: student.name,
        item: 'Missing documents',
          area: 'Compliance',
          status: 'missing',
          dueDate: null,
          assignedTo: student.keyWorker?.name || 'Unassigned'
        }];
      }
      return getDocumentAlerts(docs).map((doc) => ({
        id: `comp-${student.id}-${doc.id || doc.name}`,
        studentId: student.id,
        studentName: student.name,
        item: doc.name,
        area: doc.category || doc.type || 'Policy',
        status: doc.status,
        dueDate: doc.dueDate || doc.expiryDate,
        assignedTo: student.keyWorker?.name || 'Unassigned',
        sourceId: doc.id
      }));
    });
  }, [students, mode]);

  const complianceKpis = useMemo(() => {
    if (mode !== 'compliance') return [];
    const total = complianceItems.length;
    const overdue = complianceItems.filter((item) => item.status === 'overdue').length;
    const dueSoon = complianceItems.filter((item) => item.status === 'due-soon' || item.status === 'due-30').length;
    const critical = complianceItems.filter((item) => ['missing', 'overdue'].includes(item.status)).length;
    return [
      { id: 'total', label: 'Total compliance items', value: total },
      { id: 'overdue', label: 'Overdue', value: overdue, badge: { label: 'Action', variant: 'danger' } },
      { id: 'dueSoon', label: 'Due soon (7/30 days)', value: dueSoon },
      { id: 'critical', label: 'Critical non-compliance', value: critical, badge: { label: 'Urgent', variant: 'danger' } }
    ];
  }, [complianceItems, mode]);

  const safeguardingItems = useMemo(() => {
    if (mode !== 'safeguarding') return [];
    return students.flatMap((student) =>
      buildSafeguardingEvents(student).map((event) => ({
        id: `saf-${student.id}-${event.id}`,
        studentId: student.id,
        studentName: student.name,
        event,
        type: event.type,
        severity: event.severity,
        status: event.status,
        owner: event.assignedOwner || 'Unassigned',
        reviewDate: event.reviewDate,
        category: event.categoryLabel || event.category || 'Safeguarding'
      }))
    );
  }, [students, mode]);

  const safeguardingKpis = useMemo(() => {
    if (mode !== 'safeguarding') return [];
    const active = safeguardingItems.filter((item) => isActiveSafeguarding(item.event)).length;
    const critical = safeguardingItems.filter((item) => ['high', 'critical'].includes(item.severity)).length;
    const overdueReviews = safeguardingItems.filter((item) => {
      if (!item.reviewDate) return false;
      const reviewDate = parseDateValue(item.reviewDate);
      return reviewDate && reviewDate < new Date() && isActiveSafeguarding(item.event);
    }).length;
    const unassigned = safeguardingItems.filter((item) => !item.event.assignedOwner).length;
    return [
      { id: 'active', label: 'Active safeguarding cases', value: active },
      { id: 'critical', label: 'Critical', value: critical, badge: { label: 'Immediate', variant: 'danger' } },
      { id: 'overdue', label: 'Overdue reviews', value: overdueReviews },
      { id: 'unassigned', label: 'Unassigned cases', value: unassigned, badge: { label: 'Assign', variant: 'warning' } }
    ];
  }, [safeguardingItems, mode]);

  const filteredCompliance = useMemo(() => {
    if (mode !== 'compliance') return [];
    return complianceItems.filter((item) => {
      if (activeKpi === 'overdue' && item.status !== 'overdue') return false;
      if (activeKpi === 'dueSoon' && !['due-soon', 'due-30'].includes(item.status)) return false;
      if (activeKpi === 'critical' && !['missing', 'overdue'].includes(item.status)) return false;
      if (!searchQuery) return true;
      const haystack = `${item.studentName} ${item.item} ${item.area}`.toLowerCase();
      return haystack.includes(searchQuery.toLowerCase());
    });
  }, [complianceItems, searchQuery, mode, activeKpi]);

  const filteredSafeguarding = useMemo(() => {
    if (mode !== 'safeguarding') return [];
    return safeguardingItems.filter((item) => {
      if (activeKpi === 'critical' && !['high', 'critical'].includes(item.severity)) return false;
      if (activeKpi === 'overdue') {
        const reviewDate = parseDateValue(item.reviewDate);
        if (!reviewDate || reviewDate >= new Date()) return false;
      }
      if (activeKpi === 'unassigned' && item.event.assignedOwner) return false;
      if (!searchQuery) return true;
      const haystack = `${item.studentName} ${item.type} ${item.category}`.toLowerCase();
      return haystack.includes(searchQuery.toLowerCase());
    }).sort((a, b) => {
      const severityOrder = { critical: 3, high: 2, medium: 1, low: 0 };
      const severityDelta = (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
      if (severityDelta !== 0) return severityDelta;
      const overdueA = parseDateValue(a.reviewDate) || new Date(0);
      const overdueB = parseDateValue(b.reviewDate) || new Date(0);
      return overdueA - overdueB;
    });
  }, [safeguardingItems, searchQuery, mode, activeKpi]);

  const activeRows = mode === 'students' ? studentRows : mode === 'compliance' ? filteredCompliance : filteredSafeguarding;

  const activeItem = useMemo(() => {
    if (!activeItemId) return null;
    if (mode === 'students') return students.find((student) => student.id === activeItemId);
    if (mode === 'compliance') return complianceItems.find((item) => item.id === activeItemId);
    return safeguardingItems.find((item) => item.id === activeItemId);
  }, [activeItemId, mode, students, complianceItems, safeguardingItems]);

  const complianceDocument = useMemo(() => {
    if (mode !== 'compliance' || !activeItem) return null;
    const student = students.find((entry) => entry.id === activeItem.studentId);
    if (!student) return null;
    const docs = [...(student.documents || []), ...(student.certificates || [])].map((doc) => (doc.toJSON ? doc.toJSON() : doc));
    return docs.find((doc) => doc.id === activeItem.sourceId) || null;
  }, [activeItem, mode, students]);

  const complianceEvidence = useMemo(() => {
    if (!complianceDocument) return [];
    const versions = (complianceDocument.versions || []).map((version, index) => ({
      id: `${complianceDocument.id || 'doc'}-${index}`,
      name: version.fileName || 'Evidence file',
      uploadedAt: version.uploadedAt,
      uploadedBy: version.uploadedBy
    }));
    if (versions.length > 0) return versions;
    if (complianceDocument.fileName) {
      return [
        {
          id: `${complianceDocument.id || 'doc'}-file`,
          name: complianceDocument.fileName,
          uploadedAt: complianceDocument.updatedAt || complianceDocument.createdAt || '',
          uploadedBy: complianceDocument.uploadedBy || ''
        }
      ];
    }
    return [];
  }, [complianceDocument]);

  useEffect(() => {
    setUploadStatus('');
    setUploading(false);
  }, [activeItemId, mode]);

  useEffect(() => {
    if (mode !== 'compliance') return;
    setComplianceNoteDraft(complianceDocument?.notes || '');
    setComplianceNoteStatus('');
  }, [mode, activeItemId, complianceDocument]);

  const toggleSelectAll = (value) => {
    if (!value) {
      setSelectedIds(new Set());
      return;
    }
    const next = new Set(activeRows.map((row) => row.id || row.studentId));
    setSelectedIds(next);
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openDrawer = (id) => {
    setActiveItemId(id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setActiveItemId(null);
    setDrawerOpen(false);
  };

  const handleComplianceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || mode !== 'compliance' || !activeItem) return;
    const student = students.find((entry) => entry.id === activeItem.studentId);
    if (!student) {
      setUploadStatus('Student not found.');
      return;
    }

    setUploading(true);
    setUploadStatus('Uploading evidence...');

    let uploadedFile;
    try {
      uploadedFile = await readFileAsStoredAttachment(file, user?.name || 'System', {
        entityType: 'student',
        entityId: student.id,
        entityName: student.name,
        category: activeItem.area || 'compliance',
        documentType: activeItem.item || 'evidence'
      });
    } catch (error) {
      setUploadStatus(error.message || 'Upload failed.');
      setUploading(false);
      event.target.value = '';
      return;
    }

    const timestamp = uploadedFile.uploadedAt || new Date().toISOString();
    const sizeLabel = uploadedFile.size || `${Math.round(file.size / 1024)} KB`;
    const nextVersion = {
      version: (complianceDocument?.versions?.length || 0) + 1,
      uploadedBy: user?.name || 'System',
      uploadedAt: timestamp,
      fileName: file.name,
      fileType: file.type || 'file',
      fileSize: sizeLabel,
      fileUrl: uploadedFile.fileUrl || '',
      storageId: uploadedFile.id || '',
      relativePath: uploadedFile.relativePath || ''
    };

    if (complianceDocument?.id) {
      const docId = complianceDocument.id;
      const inDocuments = (student.documents || []).some((doc) => doc.id === docId);
      const inCertificates = (student.certificates || []).some((doc) => doc.id === docId);

      if (inDocuments || !inCertificates) {
        const updateDoc = (doc) => {
          if (doc.id !== docId) return doc;
          return {
            ...doc,
            versions: [...(doc.versions || []), nextVersion],
            metadata: {
              ...(doc.metadata || {}),
              type: file.type || 'file',
              size: sizeLabel,
              fileUrl: uploadedFile.fileUrl || doc.metadata?.fileUrl || '',
              relativePath: uploadedFile.relativePath || doc.metadata?.relativePath || ''
            },
            notes: complianceNoteDraft?.trim() || doc.notes || '',
            updatedAt: timestamp
          };
        };
        controller.updateStudent(
          student.id,
          {
            documents: (student.documents || []).map(updateDoc),
            certificates: (student.certificates || []).map(updateDoc)
          },
          user
        );
      } else {
        const updateCertificate = (cert) => (
          cert.id === docId
            ? { ...cert, fileName: file.name, notes: complianceNoteDraft?.trim() || cert.notes || '', updatedAt: timestamp }
            : cert
        );
        controller.updateStudent(
          student.id,
          { certificates: (student.certificates || []).map(updateCertificate) },
          user
        );
      }
    } else {
      const name = activeItem.item || file.name.replace(/\.[^/.]+$/, '');
      const docId = `doc-${Math.random().toString(36).slice(2, 9)}`;
      controller.addDocument(
        student.id,
        {
          id: docId,
          name,
          tags: [activeItem.area || 'Compliance'],
          visibility: ['Admin', 'Manager', 'Staff'],
          metadata: { type: file.type || 'file', size: sizeLabel },
          dueDate: activeItem.dueDate || null,
          notes: complianceNoteDraft?.trim() || '',
          versions: [nextVersion]
        },
        user
      );
      setActiveItemId(`comp-${student.id}-${docId}`);
    }

    setUploadStatus(`Evidence attached: ${file.name}`);
    setUploading(false);
    event.target.value = '';
  };

  const markComplianceComplete = () => {
    if (mode !== 'compliance' || !activeItem) return;
    const student = students.find((entry) => entry.id === activeItem.studentId);
    if (!student || !complianceDocument?.id) return;
    const docId = complianceDocument.id;
    const timestamp = new Date().toISOString();
    const updateDoc = (doc) => {
      if (doc.id !== docId) return doc;
      return {
        ...doc,
        metadata: {
          ...(doc.metadata || {}),
          complianceResolved: true,
          completedAt: timestamp
        },
        updatedAt: timestamp
      };
    };
    controller.updateStudent(
      student.id,
      {
        documents: (student.documents || []).map(updateDoc),
        certificates: (student.certificates || []).map(updateDoc)
      },
      user
    );
    closeDrawer();
  };

  const saveComplianceNotes = () => {
    if (mode !== 'compliance' || !activeItem) return;
    const student = students.find((entry) => entry.id === activeItem.studentId);
    if (!student) return;
    const trimmed = complianceNoteDraft.trim();
    if (complianceDocument?.id) {
      const docId = complianceDocument.id;
      const updateDoc = (doc) => (doc.id === docId ? { ...doc, notes: trimmed } : doc);
      controller.updateStudent(
        student.id,
        {
          documents: (student.documents || []).map(updateDoc),
          certificates: (student.certificates || []).map(updateDoc)
        },
        user
      );
      setComplianceNoteStatus('Notes saved.');
      return;
    }

    if (!trimmed) return;
    controller.addDocument(
      student.id,
      {
        name: activeItem.item || 'Compliance evidence',
        tags: [activeItem.area || 'Compliance'],
        visibility: ['Admin', 'Manager', 'Staff'],
        metadata: {},
        dueDate: activeItem.dueDate || null,
        notes: trimmed,
        versions: []
      },
      user
    );
    setComplianceNoteStatus('Notes saved.');
  };

  const applyKeyWorker = () => {
    if (!assignTargetId) return;
    const staff = staffList.find((item) => item.id === assignTargetId);
    if (!staff) return;

    const notifyAssignment = (summaryLines) => {
      if (!summaryLines.length) return;
      messageService.send({
        subject: `New assignment: ${summaryLines[0]}`,
        body: summaryLines.join('\n'),
        fromUser: user || { id: 'system', name: 'SafeguardPro' },
        recipients: [{ id: staff.id, name: staff.name, type: 'staff' }],
        references: [],
        assignment: true
      });
    };

    if (mode === 'students') {
      const studentNames = students.filter((student) => selectedIds.has(student.id)).map((student) => student.name);
      students
        .filter((student) => selectedIds.has(student.id))
        .forEach((student) => {
          controller.updateStudent(student.id, { keyWorker: { id: staff.id, name: staff.name, notes: '', goals: [] } });
        });
      notifyAssignment([
        `Assigned as key worker for ${studentNames.length} student(s).`,
        ...studentNames.map((name) => `- ${name}`)
      ]);
    } else if (mode === 'compliance') {
      const items = complianceItems.filter((item) => selectedIds.has(item.id));
      complianceItems
        .filter((item) => selectedIds.has(item.id))
        .forEach((item) => {
          const student = students.find((s) => s.id === item.studentId);
          if (!student) return;
          controller.updateStudent(student.id, { keyWorker: { id: staff.id, name: staff.name, notes: '', goals: [] } });
        });
      notifyAssignment([
        `Compliance items assigned (${items.length})`,
        ...items.map((item) => `- ${item.item} · ${item.studentName}`)
      ]);
    } else {
      const items = safeguardingItems.filter((item) => selectedIds.has(item.id));
      safeguardingItems
        .filter((item) => selectedIds.has(item.id))
        .forEach((item) => {
          const student = students.find((s) => s.id === item.studentId);
          if (!student) return;
          const updateEvent = (event) => (event.id === item.event.id ? { ...event, assignedOwner: staff.name } : event);
          controller.updateStudent(student.id, {
            safeguardingEvents: (student.safeguardingEvents || []).map(updateEvent),
            medicalEvents: (student.medicalEvents || []).map(updateEvent),
            policeEvents: (student.policeEvents || []).map(updateEvent)
          });
        });
      notifyAssignment([
        `Safeguarding cases assigned (${items.length})`,
        ...items.map((item) => `- ${item.type} · ${item.studentName}`)
      ]);
    }

    setAssignModalOpen(false);
    setAssignTargetId('');
  };

  const applyReviewDate = () => {
    if (!reviewDate) return;
    if (mode === 'compliance') {
      complianceItems
        .filter((item) => selectedIds.has(item.id))
        .forEach((item) => {
          const student = students.find((s) => s.id === item.studentId);
          if (!student || !item.sourceId) return;
          const updateDoc = (doc) => (doc.id === item.sourceId ? { ...doc, dueDate: reviewDate } : doc);
          controller.updateStudent(student.id, {
            documents: (student.documents || []).map(updateDoc),
            certificates: (student.certificates || []).map(updateDoc)
          });
        });
    } else {
      safeguardingItems
        .filter((item) => selectedIds.has(item.id))
        .forEach((item) => {
          const student = students.find((s) => s.id === item.studentId);
          if (!student) return;
          const updateEvent = (event) => (event.id === item.event.id ? { ...event, reviewDate } : event);
          controller.updateStudent(student.id, {
            safeguardingEvents: (student.safeguardingEvents || []).map(updateEvent),
            medicalEvents: (student.medicalEvents || []).map(updateEvent),
            policeEvents: (student.policeEvents || []).map(updateEvent)
          });
        });
    }
    setReviewDateModal(false);
    setReviewDate('');
  };

  const bulkActions = useMemo(() => {
    if (mode === 'students') {
      return [
        {
          label: 'Assign support worker',
          onClick: () => setAssignModalOpen(true),
          disabled: selectedIds.size === 0
        },
        {
          label: 'Flag for review',
          onClick: () => {
            students
              .filter((student) => selectedIds.has(student.id))
              .forEach((student) => controller.updateStudent(student.id, { status: 'Under Review' }));
          },
          disabled: selectedIds.size === 0
        },
        {
          label: 'Export list',
          onClick: () => {
            const rows = students
              .filter((student) => selectedIds.has(student.id))
              .map((student) => [student.name, student.reference, student.riskLevel, student.attendance?.rate || 0]);
            const csv = ['Name,ID,Risk,Attendance', ...rows.map((row) => row.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'students_export.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          },
          disabled: selectedIds.size === 0
        }
      ];
    }
    if (mode === 'compliance') {
      return [
        {
          label: 'Assign owner',
          onClick: () => setAssignModalOpen(true),
          disabled: selectedIds.size === 0
        },
        {
          label: 'Set due date',
          onClick: () => setReviewDateModal(true),
          disabled: selectedIds.size === 0
        },
        {
          label: 'Export evidence',
          onClick: () => {
            const rows = complianceItems
              .filter((item) => selectedIds.has(item.id))
              .map((item) => [
                item.studentName,
                item.item,
                item.area,
                statusMeta[item.status]?.label || item.status,
                item.dueDate || ''
              ]);
            const csv = ['Student,Item,Area,Status,Due', ...rows.map((row) => row.join(','))].join('\\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'compliance_evidence.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          },
          disabled: selectedIds.size === 0
        }
      ];
    }
    return [
      {
        label: 'Assign owner',
        onClick: () => setAssignModalOpen(true),
        disabled: selectedIds.size === 0
      },
      {
        label: 'Set review date',
        onClick: () => setReviewDateModal(true),
        disabled: selectedIds.size === 0
      },
      {
        label: 'Close cases',
        onClick: () => {
          safeguardingItems
            .filter((item) => selectedIds.has(item.id))
            .forEach((item) => {
              const student = students.find((s) => s.id === item.studentId);
              if (!student) return;
              const updateEvent = (event) => (event.id === item.event.id ? { ...event, status: 'closed' } : event);
              controller.updateStudent(student.id, {
                safeguardingEvents: (student.safeguardingEvents || []).map(updateEvent),
                medicalEvents: (student.medicalEvents || []).map(updateEvent),
                policeEvents: (student.policeEvents || []).map(updateEvent)
              });
            });
        },
        disabled: selectedIds.size === 0
      }
    ];
  }, [mode, selectedIds, students, controller, safeguardingItems, complianceItems]);

  const kpiItems = mode === 'students' ? studentKpis : mode === 'compliance' ? complianceKpis : safeguardingKpis;

  if (blockedPackName) {
    return <PackDisabledPanel packName={blockedPackName} />;
  }

  return (
    <div className="space-y-6">
      <div className="sp-page-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>
            {mode === 'students' ? 'Students Workspace' : mode === 'compliance' ? 'Compliance Workspace' : 'Safeguarding Workspace'}
          </h2>
          <p className="sp-muted">{mode === 'students'
            ? 'Quickly identify risk, attendance issues, and safeguarding flags.'
            : mode === 'compliance'
              ? 'Track compliance items, due dates, and readiness for audits.'
              : 'Manage safeguarding cases with clear risk and action visibility.'}
          </p>
        </div>
        <div className="sp-page-actions">
          {mode === 'safeguarding' && (
            <Button size="sm" onClick={() => navigate('/safeguarding/new')}>
              Log Safeguarding Report
            </Button>
          )}
          {mode === 'students' && (
            <Button size="sm" onClick={() => navigate('/students/enrol')}>Add student</Button>
          )}
        </div>
      </div>

      <WorkspaceKpis items={kpiItems} activeKpi={activeKpi} onSelect={setActiveKpi} />

      <div className="sp-filter-bar">
        <div className="sp-filter-main">
          <div>
            <label className="sp-label">Search</label>
            <div className="sp-search mt-2">
              <Icons.Search />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={mode === 'compliance' ? 'Search compliance items' : 'Search students'}
              />
            </div>
          </div>
          <div className="sp-filter-controls">
            {mode === 'students' && (
              <>
                <div>
                  <label className="sp-label">Risk</label>
                  <select
                    className="sp-select mt-2"
                    value={filters.risk}
                    onChange={(event) => setFilters((prev) => ({ ...prev, risk: event.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="sp-label">Attendance</label>
                  <select
                    className="sp-select mt-2"
                    value={filters.attendance}
                    onChange={(event) => setFilters((prev) => ({ ...prev, attendance: event.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="below">Below threshold</option>
                  </select>
                </div>
                <div>
                  <label className="sp-label">Safeguarding</label>
                  <select
                    className="sp-select mt-2"
                    value={filters.safeguarding}
                    onChange={(event) => setFilters((prev) => ({ ...prev, safeguarding: event.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="active">Active only</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="sp-workspace-grid">
        <div>
          {activeRows.length === 0 ? (
            <Card className="p-6">
              <p className="text-sm sp-muted">No results for the current filters.</p>
            </Card>
          ) : (
            <div className={`sp-table ${mode === 'students'
              ? 'sp-table--students-workspace'
              : mode === 'compliance'
                ? 'sp-table--compliance-workspace'
                : 'sp-table--safeguarding-workspace'}`}>
              <div className="sp-row sp-row--header">
                <div>
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === activeRows.length}
                    onChange={(event) => toggleSelectAll(event.target.checked)}
                    aria-label="Select all"
                  />
                </div>
                {mode === 'students' && (
                  <>
                    <div>Student</div>
                    <div>Risk</div>
                    <div>Attendance</div>
                    <div>Safeguarding</div>
                    <div>Key worker</div>
                    <div>Actions</div>
                  </>
                )}
                {mode === 'compliance' && (
                  <>
                    <div>Compliance item</div>
                    <div>Area</div>
                    <div>Status</div>
                    <div>Assigned to</div>
                    <div>Due / expiry</div>
                    <div>Actions</div>
                  </>
                )}
                {mode === 'safeguarding' && (
                  <>
                    <div>Case type</div>
                    <div>Risk</div>
                    <div>Status</div>
                    <div>Student</div>
                    <div>Owner</div>
                    <div>Review due</div>
                    <div>Actions</div>
                  </>
                )}
              </div>
              {activeRows.map((row) => {
                const rowId = row.id || row.studentId;
                const isSelected = selectedIds.has(rowId);
                if (mode === 'students') {
                  const safeguardingOpen = buildSafeguardingEvents(row).filter((event) => isActiveSafeguarding(event));
                  return (
                    <div
                      key={row.id}
                      className={`sp-row sp-row--inventory ${row.riskLevel === 'critical' ? 'sp-row--critical' : ''}`}
                      onClick={() => openDrawer(row.id)}
                    >
                      <div onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(row.id)}
                          aria-label={`Select ${row.name}`}
                        />
                      </div>
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{row.name}</div>
                        <div className="text-xs sp-muted">ID: {row.reference}</div>
                      </div>
                      <div>
                        <Badge variant={row.riskLevel === 'critical' ? 'critical' : row.riskLevel === 'high' ? 'high' : 'medium'}>
                          {row.riskLevel}
                        </Badge>
                      </div>
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{row.attendance?.rate ?? 0}%</div>
                      </div>
                      <div>
                        <Badge variant={safeguardingOpen.length ? 'warning' : 'active'}>
                          {safeguardingOpen.length ? `${safeguardingOpen.length} active` : 'Clear'}
                        </Badge>
                      </div>
                      <div className="text-sm">{row.keyWorker?.name || 'Unassigned'}</div>
                      <div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/students/${row.id}/profile`);
                          }}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  );
                }

                if (mode === 'compliance') {
                  const meta = statusMeta[row.status] || statusMeta.compliant;
                  return (
                    <div
                      key={row.id}
                      className={`sp-row sp-row--inventory ${['overdue', 'missing'].includes(row.status) ? 'sp-row--critical' : ''}`}
                      onClick={() => openDrawer(row.id)}
                    >
                      <div onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(row.id)}
                          aria-label={`Select ${row.item}`}
                        />
                      </div>
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{row.item}</div>
                        <div className="text-xs sp-muted">{row.studentName}</div>
                      </div>
                      <div>{row.area}</div>
                      <div>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </div>
                      <div className="text-sm">{row.assignedTo}</div>
                      <div className="text-sm">{formatDate(row.dueDate) || '—'}</div>
                      <div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDrawer(row.id);
                          }}
                        >
                          {row.status === 'missing' ? 'Upload' : 'Review'}
                        </Button>
                      </div>
                    </div>
                  );
                }

                const severity = row.severity;
                const isCritical = ['critical', 'high'].includes(severity);
                return (
                  <div
                    key={row.id}
                    className={`sp-row sp-row--incident ${isCritical ? 'sp-row--critical' : ''}`}
                    onClick={() => openDrawer(row.id)}
                  >
                    <div onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(row.id)}
                        aria-label={`Select ${row.type}`}
                      />
                    </div>
                    <div>
                      <div className="sp-value" style={{ fontWeight: 600 }}>{row.type}</div>
                      <div className="text-xs sp-muted">{row.category}</div>
                    </div>
                    <div>
                      <Badge variant={severity === 'critical' ? 'critical' : severity === 'high' ? 'high' : 'medium'}>
                        {severity}
                      </Badge>
                    </div>
                    <div>
                      <Badge variant={row.status === 'open' ? 'warning' : row.status === 'monitoring' ? 'review' : 'inactive'}>
                        {row.status}
                      </Badge>
                    </div>
                    <div>{row.studentName}</div>
                    <div>{row.owner}</div>
                    <div>{formatDate(row.reviewDate) || '—'}</div>
                    <div>
                      <Button size="sm" variant="ghost" onClick={(event) => event.stopPropagation()}>
                        Review
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Card className={`sp-workspace-drawer ${drawerOpen ? 'is-open' : ''}`}>
          {!activeItem ? (
            <div className="sp-drawer-empty">Select a row to view details.</div>
          ) : mode === 'students' ? (
            <div className="space-y-4">
              <div className="sp-drawer-header">
                <div>
                  <div className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{activeItem.name}</div>
                  <div className="text-xs sp-muted">ID: {activeItem.reference}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={closeDrawer}>Close</Button>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-drawer-badges">
                  <Badge variant={activeItem.riskLevel === 'critical' ? 'critical' : 'high'}>{activeItem.riskLevel}</Badge>
                  <Badge variant={activeItem.status === 'Under Review' ? 'review' : 'active'}>{activeItem.status}</Badge>
                </div>
                <div className="sp-drawer-meta">
                  <div>
                    <div className="sp-label">Attendance</div>
                    <div className="sp-value">{activeItem.attendance?.rate ?? 0}%</div>
                  </div>
                  <div>
                    <div className="sp-label">Safeguarding</div>
                    <div className="sp-value">
                      {buildSafeguardingEvents(activeItem).filter((event) => isActiveSafeguarding(event)).length} active
                    </div>
                  </div>
                  <div>
                    <div className="sp-label">Key worker</div>
                    <div className="sp-value">{activeItem.keyWorker?.name || 'Unassigned'}</div>
                  </div>
                </div>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Quick links</div>
                <div className="sp-drawer-actions">
                  <Button size="sm" onClick={() => navigate(`/students/${activeItem.id}/profile`)}>Open profile</Button>
                  <Button size="sm" variant="secondary" onClick={() => navigate(`/students/${activeItem.id}/dashboard`)}>
                    Open dashboard
                  </Button>
                </div>
              </div>
            </div>
          ) : mode === 'compliance' ? (
            <div className="space-y-4">
              <div className="sp-drawer-header">
                <div>
                  <div className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{activeItem.item}</div>
                  <div className="text-xs sp-muted">{activeItem.studentName}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={closeDrawer}>Close</Button>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-drawer-meta">
                  <div>
                    <div className="sp-label">Status</div>
                    <div className="sp-value">{statusMeta[activeItem.status]?.label || 'Compliant'}</div>
                  </div>
                  <div>
                    <div className="sp-label">Due / expiry</div>
                    <div className="sp-value">{formatDate(activeItem.dueDate) || 'Not set'}</div>
                  </div>
                  <div>
                    <div className="sp-label">Owner</div>
                    <div className="sp-value">{activeItem.assignedTo}</div>
                  </div>
                </div>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Evidence</div>
                {activeItem.status === 'missing' && (
                  <div className="sp-panel sp-panel--muted p-3 text-sm mt-2">
                    <div className="sp-value" style={{ fontWeight: 600 }}>Missing document</div>
                    <div className="text-xs sp-muted mt-1">
                      This compliance item does not have evidence attached. Upload the missing document below.
                    </div>
                  </div>
                )}
                {complianceEvidence.length > 0 ? (
                  <div className="space-y-2">
                    {complianceEvidence.map((file) => (
                      <div key={file.id} className="sp-panel sp-panel--muted p-3 text-sm">
                        <div className="sp-value" style={{ fontWeight: 600 }}>{file.name}</div>
                        <div className="text-xs sp-muted">
                          {file.uploadedAt ? `Uploaded ${formatDate(file.uploadedAt)}` : 'Upload date not recorded'}
                          {file.uploadedBy ? ` · ${file.uploadedBy}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm sp-muted">No evidence files attached yet.</div>
                )}
                <div className="mt-3">
                  <label className="sp-label">Upload evidence</label>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() => complianceFileRef.current?.click()}
                      disabled={uploading}
                    >
                      {activeItem.status === 'missing' ? 'Upload missing document' : 'Upload evidence'}
                    </Button>
                    <input
                      ref={complianceFileRef}
                      type="file"
                      className="sp-input"
                      style={{ display: 'none' }}
                      onChange={handleComplianceUpload}
                      disabled={uploading}
                      aria-label="Upload compliance evidence"
                    />
                  </div>
                  <div className="text-xs sp-muted mt-2">
                    Accepted formats: PDF, images, or documents. Evidence metadata is stored in this demo.
                  </div>
                  {uploadStatus && <div className="text-xs sp-muted mt-2">{uploadStatus}</div>}
                </div>
                {activeItem.status === 'missing' && complianceEvidence.length > 0 && (
                  <div className="mt-3">
                    <Button size="sm" onClick={markComplianceComplete}>
                      Mark complete
                    </Button>
                    <p className="text-xs sp-muted mt-1">
                      Marking complete will remove this item from the compliance list.
                    </p>
                  </div>
                )}
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Evidence notes</div>
                <textarea
                  className="sp-textarea mt-2 w-full"
                  rows={3}
                  value={complianceNoteDraft}
                  onChange={(event) => setComplianceNoteDraft(event.target.value)}
                  placeholder="Add notes about this document (reason for missing, who to follow up with, etc.)."
                />
                <div className="mt-2 flex items-center gap-2">
                  <Button size="sm" variant="secondary" onClick={saveComplianceNotes}>
                    Save notes
                  </Button>
                  {complianceNoteStatus && <span className="text-xs sp-muted">{complianceNoteStatus}</span>}
                </div>
              </div>
              <div className="sp-drawer-actions">
                <Button size="sm" onClick={() => setAssignModalOpen(true)}>Assign owner</Button>
                <Button size="sm" variant="secondary" onClick={() => setReviewDateModal(true)}>Set due date</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="sp-drawer-header">
                <div>
                  <div className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{activeItem.type}</div>
                  <div className="text-xs sp-muted">{activeItem.studentName}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={closeDrawer}>Close</Button>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-drawer-badges">
                  <Badge variant={activeItem.severity === 'critical' ? 'critical' : 'high'}>{activeItem.severity}</Badge>
                  <Badge variant={activeItem.status === 'open' ? 'warning' : 'inactive'}>{activeItem.status}</Badge>
                </div>
                <div className="sp-drawer-meta">
                  <div>
                    <div className="sp-label">Owner</div>
                    <div className="sp-value">{activeItem.owner}</div>
                  </div>
                  <div>
                    <div className="sp-label">Review due</div>
                    <div className="sp-value">{formatDate(activeItem.reviewDate) || 'Not set'}</div>
                  </div>
                </div>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Case summary</div>
                <div className="text-sm sp-muted">{activeItem.event?.description || 'No description provided.'}</div>
              </div>
              <div className="sp-drawer-actions">
                <Button size="sm" onClick={() => setAssignModalOpen(true)}>Assign owner</Button>
                <Button size="sm" variant="secondary" onClick={() => setReviewDateModal(true)}>Set review date</Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {drawerOpen && <div className="sp-drawer-overlay" onClick={closeDrawer} aria-hidden="true" />}

      <BulkActionBar selectedCount={selectedIds.size} actions={bulkActions} />

      <Modal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Assign owner</h3>
              <p className="text-sm sp-muted">Assign a support worker to selected items.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setAssignModalOpen(false)}>Close</Button>
          </div>
          <SearchableSelect
            value={assignTargetId}
            options={staffOptions}
            placeholder="Search staff"
            emptyLabel="No staff found."
            ariaLabel="Search staff"
            onChange={setAssignTargetId}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button onClick={applyKeyWorker} disabled={!assignTargetId}>Assign</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={reviewDateModal} onClose={() => setReviewDateModal(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Set review / due date</h3>
              <p className="text-sm sp-muted">Apply a date to selected items.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setReviewDateModal(false)}>Close</Button>
          </div>
          <DateSelect value={reviewDate} onChange={setReviewDate} className="w-full" />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReviewDateModal(false)}>Cancel</Button>
            <Button onClick={applyReviewDate} disabled={!reviewDate}>Apply</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
